import { inject, Injectable } from "@angular/core";
import {
  ActivatedRouteSnapshot,
  CanActivateFn,
  Router,
  RouterStateSnapshot,
} from "@angular/router";
import { Observable, from, of } from "rxjs";
import { map, switchMap, tap, catchError } from "rxjs/operators";
import { AuthApi } from '@TheGreenHouseCompany/vegiwise-sdk-typescript/dist/sdk/auth/auth.api';
import { UserApi } from "@TheGreenHouseCompany/vegiwise-sdk-typescript/dist/sdk/user/user-api";
import { AddressToUserApi } from "@TheGreenHouseCompany/vegiwise-sdk-typescript/dist/sdk/location/address-to-user/address-to-user-api";
import { AddressToUserListRequest } from "@TheGreenHouseCompany/vegiwise-sdk-typescript/dist/generated/@dto/requests/location/address-to-user-list.request";

@Injectable({
  providedIn: "root",
})
export class PermissionsService {
  private authApi: AuthApi
  private userApi: UserApi
  private addressToUserApi: AddressToUserApi

  constructor(private router: Router) {
    this.authApi = new AuthApi();
    this.userApi = new UserApi();
    this.addressToUserApi = new AddressToUserApi();
  }

  /**
   * Navigation guard that ensures:
   * 1. User is authenticated
   * 2. User has an address (either in localStorage or in their profile)
   * 3. Redirects to onboarding if no address is found
   */
  canActivate(
    route: ActivatedRouteSnapshot,
    state: RouterStateSnapshot
  ): Observable<boolean> {
    // First, check if the user is authenticated
    return this.authApi.isAuthenticate().pipe(
      map(isAuthenticated => {
        if (!isAuthenticated) {
          // If not authenticated, clear session and redirect to init page
          this.authApi.logout();
          this.authApi.memorizeURL(state.url.split("?")[0], route.queryParams);
          this.router.navigate(["/init"]);
          return false;
        }
        return true;
      }),
      switchMap(isAuthenticated => {
        if (!isAuthenticated) {
          return of(false);
        }

        // Check if user has an address in localStorage
        return from(this.authApi.getAddressUid()).pipe(
          switchMap(addressUid => {
            // If address exists in localStorage, allow navigation
            if (addressUid !== null) {
              return of(true);
            }

            // If user is already on onboarding page, allow navigation
            if (state.url === "/onboarding") {
              return of(true);
            }

            // If no address in localStorage, fetch user's addresses from API
            return from(this.userApi.getCurrentUser()).pipe(
              switchMap(user => {
                const request = new AddressToUserListRequest();
                request.userUid = user.uid;

                // Fetch user's addresses
                return from(this.addressToUserApi.list(request, 0, 10, 'fr')).pipe(
                  map(addressToUserList => {
                    if (addressToUserList.results.length > 0) {
                      // If address found, save it to localStorage and allow navigation
                      this.userApi.addUserAddressUid(addressToUserList.results[0].addressUid);
                      return true;
                    }
                    // If no address found, redirect to onboarding
                    this.router.navigate(["/onboarding"]);
                    return false;
                  })
                );
              })
            );
          }),
          catchError(error => {
            // Handle any errors during the process
            console.error("Error in navigation guard:", error);
            return of(false);
          })
        );
      })
    );
  }
}

export const NavigationGuard: CanActivateFn = (
  next: ActivatedRouteSnapshot,
  state: RouterStateSnapshot
): Observable<boolean> => {
  return inject(PermissionsService).canActivate(next, state);
};
